import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

export default function Account() {
  const { user } = useAuth();
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/orders")
      .then((r) => setOrders(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const downloadInvoice = async (orderId) => {
    const res = await fetch(`${API}/orders/${orderId}/invoice`, { credentials: "include" });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `factura-${orderId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-20 fade-in" data-testid="account-page">
      <p className="tracking-eyebrow text-[var(--eafg-muted)]">{user?.email}</p>
      <h1 className="font-serif-display text-5xl mt-3 mb-12">{t("account.title")}</h1>

      <h2 className="font-serif-display text-3xl mb-6">{t("account.orders")}</h2>
      {loading ? (
        <p className="text-[var(--eafg-muted)] tracking-eyebrow">{t("common.loading")}</p>
      ) : orders.length === 0 ? (
        <p className="text-[var(--eafg-muted)]">{t("account.empty")}</p>
      ) : (
        <table className="w-full text-left" data-testid="orders-table">
          <thead>
            <tr className="tracking-eyebrow text-[var(--eafg-muted)] border-b border-[var(--eafg-line)]">
              <th className="py-3">{t("account.orderId")}</th>
              <th className="py-3">{t("account.date")}</th>
              <th className="py-3">{t("account.status")}</th>
              <th className="py-3 text-right">{t("account.total")}</th>
              <th className="py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-[var(--eafg-line)]" data-testid={`order-row-${o.id}`}>
                <td className="py-4 font-mono text-sm">
                  {o.invoice_number || o.id.slice(0, 8).toUpperCase()}
                </td>
                <td className="py-4 text-sm text-[var(--eafg-muted)]">
                  {new Date(o.created_at).toLocaleDateString()}
                </td>
                <td className="py-4">
                  <span
                    className={`tracking-eyebrow text-xs px-2 py-1 border ${
                      o.status === "paid"
                        ? "border-[var(--eafg-copper)] text-[var(--eafg-copper)]"
                        : "border-[var(--eafg-line)] text-[var(--eafg-muted)]"
                    }`}
                  >
                    {o.status}
                  </span>
                </td>
                <td className="py-4 text-right tabular-nums">{o.amount_total.toFixed(2)} €</td>
                <td className="py-4 text-right">
                  {o.status === "paid" && (
                    <button
                      onClick={() => downloadInvoice(o.id)}
                      className="inline-flex items-center gap-2 text-sm tracking-eyebrow hover:text-[var(--eafg-copper)]"
                      data-testid={`download-invoice-${o.id}`}
                    >
                      <Download size={14} strokeWidth={1.5} /> {t("account.invoice")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
